/**
 * overlayRegistry — THE ONE LEDGER OF WHAT IS OPEN.
 *
 * Every body-level floating surface registers here while it is open, and this
 * module is the only place that answers who is above whom, who must move out of
 * whose way, who is nested inside whom, whether anything of a given band or
 * trait is up, who owns an Escape (or Enter) keystroke, and which cards an
 * opening panel closes. `overlayKinds.js` says what a
 * kind IS; this module says what is CURRENTLY on screen. Nothing else in the
 * app keeps a second record of that (design D6).
 *
 * ORDER IS ACTIVATION, NOT REGISTRATION. The last entry is the most recently
 * activated one. An entry enters at the end when it registers and is MOVED back
 * to the end whenever its `active` predicate transitions to true. That
 * distinction is load-bearing rather than cosmetic: several governed surfaces
 * render inside an Artemis message and stay mounted long after the message
 * scrolled past, so a panel opened now must outrank a card that opened five
 * minutes ago even though its component mounted first.
 *
 * WHY THAT MAKES AVOIDANCE STABLE (D3). `obstaclesFor` looks only BACKWARD in
 * activation order, so a newcomer moves out of the way of what is already
 * placed and nothing already placed moves because of the newcomer. Two overlays
 * can therefore never ping-pong: the relation is a strict order, not a mutual
 * one.
 *
 * "OPEN" MEANS REGISTERED. A surface registers when it opens and deregisters
 * when it closes, so membership in this ledger IS openness — that is what
 * `anyOpen` reads, and it is what the glance card's pointer-leave claim needs
 * (a panel still holds a card's leave while it is mid-measurement). `state` is
 * a VISIBILITY sub-state of an open overlay (D5): while it reads `'hidden'` the
 * entry keeps its band, its slot and its claim, and is skipped by
 * `obstaclesFor`, by the Escape list and by the z count — the three places
 * where an invisible surface must not act on a visible one.
 *
 * A WRITE NEVER READS THE REACTIVE LEDGER (th-rjgza). Only the queries read
 * `entries`, because they are what a consumer's effect is MEANT to depend on.
 * Registration, deregistration, promotion and `touch` read the raw `ledger`
 * and mutate through the proxy: a nested overlay deregisters from its scope's
 * disposal, which Vue runs inside the HOST's render effect with tracking on, so
 * a tracked `indexOf` there subscribed the host to the ledger the `splice` then
 * triggered, and a host that remounts its child on every render never settled.
 *
 * ESCAPE DISPATCH IS THIS MODULE'S, AND THERE IS NO SECOND STACK. The ordering
 * machinery below — the detached singleton scope, the per-entry ordering scope
 * and the `flush: 'sync'` watcher — and the dispatcher at the foot of the file
 * are the ONE copy: `useEscapeDismiss.js` is now a thin registrant of the
 * `dismissable` kind and keeps only the contract's public signature. An overlay
 * registered through the primitive with a kind carrying `escape: true` is a
 * registrant by virtue of being open, which is why an adopter of `useOverlay`
 * makes no separate Escape call and cannot claim the key twice.
 *
 * @see openspec/changes/overlay-primitive/design.md — D3, D5, D6
 * @see openspec/changes/overlay-primitive/specs/overlay-primitive/spec.md
 */

import {
  effectScope,
  getCurrentScope,
  onScopeDispose,
  ref,
  shallowReactive,
  toRaw,
  toValue,
  watch,
} from "vue";

import { BAND_STRIDE, bandBase, kindFor } from "./overlayKinds";

/**
 * The two values an entry's `state` may take. Declared HERE so no positioning
 * adapter and no composable spells the hidden literal itself (D5 — the Ruby
 * band-vocabulary ratchet reads the adapters, and they pass booleans).
 */
export const OVERLAY_STATE_OPEN = "open";
export const OVERLAY_STATE_HIDDEN = "hidden";

// ---------------------------------------------------------------------------
// THE LEDGER
// ---------------------------------------------------------------------------

/**
 * One array for the whole application, in activation order.
 *
 * `shallowReactive` rather than `reactive`: consumers track membership and
 * position, while an entry's own moving parts are already reactive on their
 * own terms (`active` and `state` are read through `toValue`, `rect` is a
 * getter called at placement time). Deep conversion would additionally try to
 * proxy the frozen roster entry and every DOMRect that passes through.
 *
 * `ledger` is the same array, raw: what every WRITE path reads (header, A WRITE
 * NEVER READS THE REACTIVE LEDGER), so a mutation subscribes no running effect.
 */
const ledger = [];
const entries = shallowReactive(ledger);

/**
 * Bumped by `touch`. A consumer re-places itself when this changes, which is
 * how an obstacle that MOVED without re-placing (the map popup on
 * `moveend`, a chrome bar on layout) reaches the overlays that avoid it.
 * Re-placing an earlier-activated overlay on the same tick is harmless: its own
 * obstacle set cannot contain the toucher, so it recomputes where it already
 * is.
 */
export const overlayVersion = ref(0);

/** `overlayVersion`'s count, kept raw so `touch` writes the ref without reading it. */
let version = 0;

/** Serial for the default entry id. Reset with the ledger so ids are stable per suite. */
let idSequence = 0;

/**
 * The owner of every ordering watcher. MUST be a DETACHED scope: registration
 * happens inside whichever component's `setup()` got there first, and an effect
 * created there dies when THAT component unmounts — silently breaking
 * precedence for every other surface in the app. This project has been bitten
 * by exactly that (th-z8wc4.19, useLogoBeacon). Each registration gets a child
 * scope inside it so deregistration releases its watcher completely; a child
 * scope splices itself out of its parent on `stop()`, which a bare `watch` stop
 * handle does not.
 */
let singletonScope = null;

function ensureSingletonScope() {
  if (!singletonScope) singletonScope = effectScope(true);
  return singletonScope;
}

/**
 * The keydown binding is maintained from exactly three places — `addEntry`,
 * `removeEntry` and the test reset — and its body lives in ESCAPE DISPATCH at
 * the foot of this file, beside the dispatcher it binds.
 */

/** Move an entry back to the end of the ledger. */
function promote(entry) {
  const index = ledger.indexOf(entry);
  if (index === -1 || index === ledger.length - 1) return;
  entries.splice(index, 1);
  entries.push(entry);
}

function addEntry(entry) {
  entries.push(entry);
  ensureSingletonScope().run(() => {
    entry.orderingScope = effectScope();
    entry.orderingScope.run(() => {
      watch(
        () => toValue(entry.active) === true,
        (isActive) => {
          if (isActive) promote(entry);
        },
        // Synchronous: a surface that opens and is dismissed within the same
        // tick — an Artemis directive that opens a pane and a keystroke that
        // follows it — must not be judged against a ledger the scheduler has
        // not flushed yet.
        { flush: "sync" },
      );
    });
  });
  syncKeydownListener();
}

function removeEntry(entry) {
  const index = ledger.indexOf(entry);
  if (index === -1) return;
  entries.splice(index, 1);
  entry.orderingScope?.stop();
  entry.orderingScope = null;
  syncKeydownListener();
}

/**
 * True in Vite's dev server and under vitest, false in a production build.
 * Declared locally for the same reason `overlayKinds.js` and `useFocusEntity.js`
 * declare their own: a four-line gate is not worth widening either module's
 * public surface. The try/catch is for node environments with no
 * `import.meta.env` at all.
 */
function isDevMode() {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env) {
      return Boolean(import.meta.env.DEV) || import.meta.env.MODE !== "production";
    }
  } catch {
    // import.meta.env unavailable — fall through to NODE_ENV.
  }
  const nodeProcess = globalThis.process;
  return Boolean(nodeProcess) && nodeProcess.env?.NODE_ENV !== "production";
}

/**
 * Is this entry VISIBLE? `state` is read through `toValue` on every query, so a
 * surface that hides mid-frame is out of the obstacle set on the next read
 * rather than on the next registration. Anything that is not exactly the hidden
 * literal counts as visible: a misspelt state must not silently take a
 * surface's Escape away from it.
 */
function isVisible(entry) {
  return toValue(entry.state) !== OVERLAY_STATE_HIDDEN;
}

// ---------------------------------------------------------------------------
// REGISTRATION
// ---------------------------------------------------------------------------

/**
 * Register an open overlay.
 *
 * The roster decides the entry's band and whether the engine places it; a
 * caller states only what the roster cannot know. A kind the roster does not
 * declare is refused by `kindFor` — it throws in development and, in a
 * production build, logs and answers `null`, in which case this returns `null`
 * and the surface renders unregistered rather than at an invented band.
 *
 * Deregistration rides on `onScopeDispose` whenever there is a scope to ride,
 * so an unmount cannot leave a stale entry holding a z slot, an obstacle rect
 * and an Escape claim. A scopeless call is accepted here (the map popup and
 * the floating-vue bridge observer both register from module-level code);
 * `useOverlay` is where a scopeless COMPONENT registration throws.
 *
 * @param {object} options
 * @param {string} [options.id] - debugging handle; defaults to a serial.
 * @param {string} options.kind - a kind `overlayKinds.js` declares.
 * @param {object|null} [options.parent] - the ENTRY this one was opened from.
 * @param {(() => DOMRect|null)|null} [options.rect] - the floating box's current
 *   rect, called at placement time. `null` for a non-visual entry.
 * @param {(() => void)|null} [options.dismiss] - how this surface closes itself.
 * @param {import('vue').MaybeRefOrGetter<boolean>} [options.active] - when this
 *   entry claims precedence; its rising edge is what promotes the entry.
 * @param {import('vue').MaybeRefOrGetter<string>} [options.state] - `'open'` or
 *   `'hidden'` (D5).
 * @param {import('vue').MaybeRefOrGetter<boolean>|null} [options.escapeWhen] -
 *   when an escape-bearing entry takes its turn in Escape order. `null` (the
 *   default) is always; otherwise the entry is eligible only while it reads
 *   `true`, so a surface whose Escape means something only in one of its states
 *   (the tour player pauses a PLAYING tour) passes the keystroke on in the rest.
 * @param {((event: KeyboardEvent) => boolean)|null} [options.onEnter] - what
 *   Enter does on this surface, offered by the same walk Escape takes (see
 *   ENTER DISPATCH below). It answers whether it acted: `true` consumes the
 *   keystroke, anything else leaves it untouched. `null` (the default) is a
 *   surface Enter passes over.
 * @returns {{ entry: object, deregister: () => void }|null}
 */
export function register({
  id,
  kind,
  parent = null,
  rect = null,
  dismiss = null,
  active = false,
  state = OVERLAY_STATE_OPEN,
  escapeWhen = null,
  onEnter = null,
} = {}) {
  const traits = kindFor(kind);
  if (!traits) return null;

  idSequence += 1;
  const entry = {
    id: id ?? `${kind}-${idSequence}`,
    kind,
    traits,
    band: traits.band,
    placed: traits.placed,
    parent,
    rect: typeof rect === "function" ? rect : () => null,
    dismiss,
    active,
    state,
    escapeWhen,
    onEnter,
    orderingScope: null,
  };

  addEntry(entry);
  if (getCurrentScope()) onScopeDispose(() => removeEntry(entry));

  return { entry, deregister: () => removeEntry(entry) };
}

/**
 * Remove an entry and stop its ordering watcher. Idempotent, so the handle's
 * `deregister` and the scope's disposal may both fire.
 */
export function deregister(entry) {
  if (!entry) return;
  removeEntry(entry);
}

// ---------------------------------------------------------------------------
// QUERIES
// ---------------------------------------------------------------------------

/** Every registered entry, in activation order. A copy: the ledger is ours. */
export function overlayEntries() {
  return [...entries];
}

/**
 * The z-index an entry paints at: its band's base plus its index among the
 * VISIBLE entries of that band, in activation order. A hidden entry keeps its
 * slot but is skipped in the count, so a mid-measurement card does not push the
 * card above it up a rung; its own z may therefore equal the next visible
 * entry's, which costs nothing because it is not painted and recomputes the
 * moment it becomes visible.
 *
 * A band-less entry (`dismissable`) has no z at all and answers `null` —
 * `bandBase(null)` is never asked.
 *
 * The index can never reach `BAND_STRIDE`, because that is the floor on the
 * distance between two adjacent rungs (th-1q1fp). If it ever did, the entry
 * would paint at the band ABOVE its own, so development throws and a production
 * build clamps to the top of the band: a broken order within one band is
 * recoverable, an inverted ladder is what this module exists to prevent.
 */
export function zIndexFor(entry) {
  if (!entry || entry.band == null) return null;

  let index = 0;
  for (let i = 0; i < entries.length; i += 1) {
    const other = entries[i];
    if (other === entry) break;
    if (other.band === entry.band && isVisible(other)) index += 1;
  }

  if (index >= BAND_STRIDE) {
    const message =
      `Overlay band ${JSON.stringify(entry.band)} holds ${index + 1} visible entries, ` +
      `at or past BAND_STRIDE (${BAND_STRIDE}) — one more would paint at the band above it. ` +
      `Raise BAND_STRIDE and the rungs in composables/overlay/overlayKinds.js.`;
    if (isDevMode()) throw new RangeError(message);
    console.error(message);
    index = BAND_STRIDE - 1;
  }

  return bandBase(entry.band) + index;
}

/**
 * Everything `entry` must place itself clear of: every other entry that is
 * visible, an obstacle by its kind, and activated BEFORE `entry` — minus
 * `entry`'s parent when its kind carries `avoidsParent: false`, because a card
 * hosted INSIDE a panel that avoided its host would be pushed out of it (D3).
 *
 * Entries, not rects: the caller reads `rect()` at the moment it places, which
 * is the only moment the rect is true.
 */
export function obstaclesFor(entry) {
  if (!entry) return [];

  const selfIndex = entries.indexOf(entry);
  const limit = selfIndex === -1 ? entries.length : selfIndex;
  const exemptParent = entry.traits?.avoidsParent === false ? entry.parent : null;

  const obstacles = [];
  for (let i = 0; i < limit; i += 1) {
    const other = entries[i];
    if (other === exemptParent) continue;
    if (!other.traits.obstacle) continue;
    if (!isVisible(other)) continue;
    obstacles.push(other);
  }
  return obstacles;
}

/**
 * The entries opened FROM this one, in activation order. Registration is what
 * opens a surface and deregistration is what closes it, so a closed child is
 * simply gone from the ledger and from this answer.
 */
export function childrenOf(entry) {
  if (!entry) return [];
  return entries.filter((other) => other.parent === entry);
}

/**
 * Is `entry` nested inside `host` — is `host` its parent, its parent's parent,
 * and so on? A pointer walk, never a ledger read: an entry's parent is named at
 * its registration and is always an entry registered before it, so the chain
 * ends and never loops. `null` hosts nothing. The host is compared RAW: a
 * caller that held its entry in a deep `ref` hands over a proxy, and an
 * identity test against a proxy fails silently.
 */
function isWithin(entry, host) {
  if (!host) return false;
  const target = toRaw(host);
  for (let node = entry.parent; node; node = node.parent) {
    if (node === target) return true;
  }
  return false;
}

/**
 * Is anything open — optionally, anything of one band, anything whose kind
 * carries one boolean trait, anything opened from within one entry, or any
 * combination? Each filter is optional and an entry must pass every filter
 * given; a trait passes only on a literal `true`, and `within` passes only for
 * an entry whose parent chain contains that entry (`childrenOf`, transitively).
 * `within: null` passes nothing, so a surface with no entry is inside nothing.
 *
 * `anyOpen({ trait: 'holdsCards', within: card })` is the glance card's
 * pointer-leave claim (`useGlanceCardPersistence.js`'s `isCardHeld`), and it is
 * PER CARD: a claimant holds the cards it was opened from and no other. A Peek
 * opened from a table cell or a map marker has no card in its chain and holds
 * none; the card's own layer stack, the member popover opened from its roster
 * and an inspector opened inside it each name it through the parent seam. It
 * reads membership, not visibility — a surface mid-measurement is open, and a
 * leave held against it must still be replayed when it closes.
 */
export function anyOpen({ band, trait, within } = {}) {
  return entries.some(
    (entry) =>
      (band === undefined || entry.band === band) &&
      (trait === undefined || entry.traits[trait] === true) &&
      (within === undefined || isWithin(entry, within)),
  );
}

/**
 * Every dismissable entry, in activation order, for the Escape dispatcher below
 * (it walks from the END — most recently activated first — and re-reads `active`
 * there, because order maintains precedence while `active` decides authority). A
 * hidden surface is skipped: it cannot be dismissed by a keystroke the user
 * cannot aim at it. So is an entry whose `escapeWhen` reads false: the keystroke
 * passes to the next entry down, as it would past a surface that never claimed
 * it. A `dismissable` entry has no band, no rect and no z, and is eligible for
 * as long as it is registered.
 */
export function escapeEntries() {
  return entries.filter(isEscapeEligible);
}

/**
 * Registered for Escape by its kind, visible enough to be aimed at, and — when
 * its registrant passed an `escapeWhen` — in the state that takes Escape. Like
 * visibility, `escapeWhen` is a DISPATCH-time reading; the listener edge reads
 * kind membership only (see `syncKeydownListener`).
 */
function isEscapeEligible(entry) {
  return (
    entry.traits.escape &&
    isVisible(entry) &&
    (entry.escapeWhen === null || toValue(entry.escapeWhen) === true)
  );
}

/** The band every glance card registers in — the cards a superseding panel closes. */
const CARD_BAND = "hover";

/**
 * ONE FLOATING PANEL AT A TIME (overlayKinds.js header choice 9, th-kxn6c.12.10):
 * dismiss every open card `entry` was NOT opened from, through the card's own
 * `dismiss`. `useOverlay.js` calls it for a `supersedesCards` kind at the
 * rising edge of `open`, before the first placement, so the panel is never
 * placed around a card that is about to close.
 *
 * A card in `entry`'s parent chain is its host and stays. A SNAPSHOT is taken
 * first, because a dismissal can unmount a card — and with it every entry
 * opened inside it — while the walk runs; a card that registers afterwards (one
 * hovered while the panel is up) is not this call's to close. It is a WRITE
 * path, so it reads the raw `ledger` (header, A WRITE NEVER READS THE REACTIVE
 * LEDGER).
 *
 * A DISMISSAL IS A SURFACE'S, AND SURFACES SHARE THEM. The three page card
 * adapters all hand over the page's one persistence machine's `dismiss`, so
 * dismissing one closes every card that machine drives. A card whose `dismiss`
 * is a host's is therefore SPARED — dismissing it would close the host this
 * panel was opened from — and each distinct `dismiss` runs once.
 *
 * @param {object} entry - the registered entry of the panel that is opening.
 * @returns {object[]} the cards whose dismissal ran, in activation order.
 */
export function supersedeCards(entry) {
  if (!entry) return [];

  const hosts = [];
  for (let node = entry.parent; node; node = node.parent) hosts.push(node);
  const spared = new Set(hosts.map((host) => host.dismiss).filter((fn) => typeof fn === "function"));

  const superseded = ledger.filter(
    (other) =>
      other !== entry &&
      other.band === CARD_BAND &&
      !hosts.includes(other) &&
      typeof other.dismiss === "function" &&
      !spared.has(other.dismiss),
  );

  const dismissed = new Set();
  for (const card of superseded) {
    if (dismissed.has(card.dismiss)) continue;
    dismissed.add(card.dismiss);
    card.dismiss();
  }
  return superseded;
}

/**
 * Report that an obstacle's rect moved without it re-placing, so every overlay
 * that avoids it re-places against the new rect. A touch from a handle that is
 * no longer registered is a no-op: there is nothing left in the ledger to
 * re-place against, and the map's `moveend` outlives its popup.
 *
 * @returns {number} the version consumers watch.
 */
export function touch(entry) {
  if (entry && ledger.indexOf(entry) === -1) return version;
  version += 1;
  overlayVersion.value = version;
  return version;
}

// ---------------------------------------------------------------------------
// ESCAPE DISPATCH
// ---------------------------------------------------------------------------
//
// ONE place decides who owns an Escape keystroke (th-2wbap, design D6). A
// surface declares its dismissal intent by being REGISTERED with an
// escape-bearing kind, and never reasons about event phases, ordering, or the
// other surfaces that might be open at the same time.
//
// This is also where the app's single editability predicate lives
// (`isEditableTarget`, re-exported by `useEscapeDismiss.js` for the four
// keyboard surfaces that ask the question without registering), because "the
// user is typing, so this keystroke is not mine" is the same question every
// dismissable surface was answering for itself — three times, three different
// ways, and they disagreed.

/**
 * Whether `dispatch` is currently bound to the document. Tracked rather than
 * inferred so the add/remove calls stay balanced; tests observe the real thing
 * by spying on `document.addEventListener` / `removeEventListener`.
 */
let listenerAttached = false;

/**
 * Bind or unbind the single document listener so an application with nothing
 * dismissable registered carries no keyboard listener at all.
 *
 * `document` BUBBLE phase, deliberately: window bubble would put us after
 * Headless UI's `Dialog` — which stops propagation there — and make us depend
 * on that ordering, and capture would put us ahead of every in-surface handler,
 * which is the shortcut plugin's own sin.
 *
 * The edge reads MEMBERSHIP of an escape-bearing kind, never visibility. This
 * runs on registration and deregistration only, so a reactive input would leave
 * the binding wrong the moment it changed: a card that registers while it is
 * still an unrevealed dwell would never bind a listener, and would then reveal
 * into a contract that cannot hear it. `traits.escape` is frozen for the
 * entry's life, so the pair of edges is exact. Visibility is a DISPATCH-time
 * reading, and `escapeEntries` is where it is taken.
 */
function syncKeydownListener() {
  if (typeof document === "undefined") return;
  const wanted = ledger.some((entry) => entry.traits.escape);
  if (wanted === listenerAttached) return;
  if (wanted) document.addEventListener("keydown", dispatch);
  else document.removeEventListener("keydown", dispatch);
  listenerAttached = wanted;
}

/** Drop the listener unconditionally, for the test reset. */
function detachKeydownListener() {
  if (typeof document !== "undefined") {
    document.removeEventListener("keydown", dispatch);
  }
  listenerAttached = false;
}

/**
 * Is `target` an editable text surface — i.e. does this keystroke belong to
 * whatever the user is typing into?
 *
 * The argument is REQUIRED, and it must be the EVENT TARGET. There is
 * deliberately no `document.activeElement` default: by the time a bubbled
 * keydown reaches the document, the focused surface's own handler has already
 * run and may have moved focus (`GlobalSearchCombobox.clearSearch` focuses the
 * map host), whereas `event.target` is fixed at dispatch. A default
 * argument here would silently hand the next author the exact bug this
 * contract exists to remove (th-hn5h0), so omitting it throws instead.
 *
 * Contenteditability is INHERITED, never read from a `contenteditable`
 * attribute on the target alone. The attribute is not inherited, so a keydown
 * whose target is a node NESTED inside a contenteditable region reports false
 * from a target-only attribute read and true from real inheritance — and the
 * nested case is the common one, because that is where the caret usually sits.
 * `INPUT` / `TEXTAREA` / `SELECT` are matched by tag because they are editable
 * without participating in contenteditable inheritance at all.
 *
 * Inheritance is resolved two ways, and the second one is not optional.
 * `HTMLElement.isContentEditable` is the browser's own answer and is used
 * wherever it exists. **jsdom does not implement it** — measured against the
 * installed jsdom 26.1.0, the property is `undefined` on every element,
 * attribute or not — so under vitest the predicate walks to the NEAREST
 * ancestor carrying a `contenteditable` declaration and reads that, which is
 * exactly what the property encodes (including the `contenteditable="false"`
 * island inside an editable region, where the nearest declaration is the
 * false one). Without the walk, every contenteditable case in every suite
 * would have to stub the property onto a fake element — a guard that asserts
 * the stub rather than the behaviour, and one that could not fail.
 *
 * A null/undefined target is NOT the same as a missing argument: a real
 * `KeyboardEvent` can carry a null target (a detached node, a synthetic event
 * in a test), and that is honestly "not editable", not a programmer error.
 *
 * @param {EventTarget|null} target - `event.target`. Required.
 * @returns {boolean}
 * @throws {TypeError} when called with no argument at all.
 */
export function isEditableTarget(target) {
  if (arguments.length === 0) {
    throw new TypeError(
      "isEditableTarget(target) requires the event target — it does not default to document.activeElement.",
    );
  }
  if (!target) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (typeof target.isContentEditable === "boolean") return target.isContentEditable;
  // No native answer (jsdom, or a non-HTML element such as SVG): resolve the
  // inheritance ourselves. `contenteditable=""` is TRUE per the HTML spec.
  const host = target.closest?.("[contenteditable]");
  if (!host) return false;
  const declared = host.getAttribute("contenteditable");
  return declared === "" || declared === "true";
}

/**
 * Is a Headless UI modal open right now?
 *
 * `aria-modal="true"` is what an open `Dialog` renders and a closed one omits
 * entirely (`"aria-modal": open ? true : undefined`, verified in the installed
 * @headlessui/vue 1.7.23 bundle), so the library's own DOM is the source of
 * truth and we keep no mirror of it that could drift. The DOM query rather than
 * this ledger's own `modal` band, deliberately: a third-party dialog is covered
 * by the query and would be invisible to the ledger.
 */
function headlessUiModalIsOpen() {
  if (typeof document === "undefined") return false;
  return document.querySelector('[aria-modal="true"]') !== null;
}

/**
 * The one Escape (and Enter) handler in the application.
 *
 * Stands down in three cases, applied ONCE here rather than by each of the
 * surfaces that would otherwise each get them slightly different:
 *
 *   1. `event.defaultPrevented` — some surface already claimed this keystroke.
 *      Covers Headless UI's `Combobox` / `Menu` / `Listbox` / `Popover`, which
 *      handle Escape on their OWN elements in the target phase and set the
 *      flag before the event ever reaches the document.
 *   2. The target is editable — Escape inside a text surface belongs to that
 *      surface. Judged from `event.target`, never `document.activeElement`.
 *   3. A Headless UI modal is open. This one is not a courtesy: `Dialog` binds
 *      its keydown on `ownerDocument.defaultView` — WINDOW, downstream of this
 *      document-bubble dispatcher — so without the stand-down our
 *      `stopPropagation()` would stop EVERY modal in the app from closing on
 *      Escape, the global modal host and the media lightbox included. It also
 *      agrees with the ledger's own principle rather than bending it: a modal
 *      is by definition the topmost layer, so deferring to it IS
 *      most-recent-wins. `Popover` is deliberately NOT covered here and does
 *      not need to be — it binds `defaultView` for FOCUS, not keydown, and
 *      emits no `aria-modal`, so rule 1 already has it.
 *
 * Past the guards, it offers the keystroke to the topmost eligible entry whose
 * `active` reads true, and consumes it on that entry's behalf (D6) —
 * `preventDefault()` for the honest "handled" signal every other consumer
 * reads, `stopPropagation()` so nothing downstream acts on the same press.
 * Consumption happens BEFORE `dismiss` runs, so "an active registrant consumed
 * the event" holds even if the surface's dismissal throws; a dismissal that
 * half-closes is a bug in the surface, not a reason to let a second surface act
 * on the same keystroke. An entry with no callable `dismiss` cannot act, so it
 * is passed over rather than allowed to swallow the key for everything beneath
 * it — the failure this contract exists to prevent.
 *
 * When nothing is eligible the event is left completely alone — neither
 * prevented nor stopped — so a surface bound outside this contract still
 * receives it.
 *
 * Enter takes the same listener and the same three stand-downs, plus a fourth
 * of its own (ENTER DISPATCH below).
 */
function dispatch(event) {
  const isEscape = event.key === "Escape";
  if (!isEscape && event.key !== "Enter") return;
  if (event.defaultPrevented) return;
  if (isEditableTarget(event.target)) return;
  if (!isEscape && isInteractiveControl(event.target)) return;
  // Last because it is the only guard that touches the DOM, and it therefore
  // runs at most once per keystroke while something is registered.
  if (headlessUiModalIsOpen()) return;

  if (isEscape) dismissTopmost(event);
  else enterTopmost(event);
}

/**
 * AN ESCAPE A TYPING SURFACE HANDS BACK — the dispatcher's second entrance.
 *
 * The dispatcher stands down on an editable target because an Escape typed in a
 * text surface belongs to that surface, and slate stops every keydown at the
 * editor before it could reach `document` anyway. So a surface that takes what
 * it owns first and ANNOUNCES the rest — `CoreMessageEditor` closes its mention
 * menu and emits `escape` for any other Escape — hands that remainder here, and
 * it takes the same walk over the same ledger: there is still one dismissal
 * order. The editable stand-down is the one guard not re-applied, because the
 * surface itself is saying the keystroke is not its own; the other two hold.
 *
 * @param {KeyboardEvent} event - the keystroke the typing surface announced.
 * @returns {boolean} whether a registrant took it.
 */
export function offerEscape(event) {
  if (event?.key !== "Escape") return false;
  if (event.defaultPrevented) return false;
  if (headlessUiModalIsOpen()) return false;

  return dismissTopmost(event);
}

/**
 * THE ONE WALK every keystroke takes: over a snapshot of the Escape-eligible
 * list, from the END — the most recently ACTIVATED entry first — to the first
 * entry whose `active` reads true and that `declares` the handler in question.
 * An entry that declares nothing for this key is passed over rather than
 * allowed to swallow it for everything beneath it.
 */
function topmostActive(declares) {
  const eligible = escapeEntries();
  for (let i = eligible.length - 1; i >= 0; i -= 1) {
    const entry = eligible[i];
    if (toValue(entry.active) !== true) continue;
    if (declares(entry)) return entry;
  }
  return null;
}

/** Escape, from both entrances. */
function dismissTopmost(event) {
  const entry = topmostActive((candidate) => typeof candidate.dismiss === "function");
  if (!entry) return false;
  event.preventDefault();
  event.stopPropagation();
  // Consuming before the dismissal runs keeps "a registrant consumed it" true
  // even when the dismissal throws or synchronously unmounts its own surface.
  entry.dismiss(event);
  return true;
}

// ---------------------------------------------------------------------------
// ENTER DISPATCH
// ---------------------------------------------------------------------------
//
// Enter rides the Escape contract rather than a second listener (v2-map-platform
// D12, th-x382ii.14): a draw session finishes its ring on Enter, and the draw
// engine's own key bindings are off, so the keystroke reaches it here, through
// the same walk over the same list (`escapeWhen` included) — there is still one
// keyboard order. `offerEscape`, the typing surfaces' entrance, stays
// Escape-only: an Enter typed in a text surface is that surface's.

/**
 * Elements whose own activation Enter is: a keyboard member who tabs to a draw
 * surface's Cancel button and presses Enter presses Cancel (stress pass 2 L6
 * F10). Judged on `event.target` alone, the element focus sits on.
 */
const INTERACTIVE_CONTROL_SELECTOR = [
  "button",
  "a[href]",
  "summary",
  "select",
  ...["button", "link", "menuitem", "option", "tab", "checkbox", "switch", "radio"].map(
    (role) => `[role="${role}"]`,
  ),
].join(", ");

/** Is `target` a control whose own action Enter is? */
function isInteractiveControl(target) {
  return typeof target?.matches === "function" && target.matches(INTERACTIVE_CONTROL_SELECTOR);
}

/**
 * Enter, to the topmost active entry that declared `onEnter`. It is consumed
 * only when `onEnter` answers `true` — the surface acted — so an Enter a surface
 * had no use for (a draw session in its edit mode) passes on untouched. Unlike
 * Escape, consumption follows the handler, because only the handler knows
 * whether it acted.
 */
function enterTopmost(event) {
  const entry = topmostActive((candidate) => typeof candidate.onEnter === "function");
  if (!entry || entry.onEnter(event) !== true) return false;
  event.preventDefault();
  event.stopPropagation();
  return true;
}

/**
 * Test-only teardown: empty the ledger, drop the keydown listener, stop every
 * ordering watcher and reset the version, so one suite's registrations cannot
 * outlive it into the next. Production has no equivalent — deregistration and
 * scope disposal are the only ways an entry leaves.
 */
export function __resetOverlayRegistryForTests() {
  entries.length = 0;
  detachKeydownListener();
  singletonScope?.stop();
  singletonScope = null;
  version = 0;
  overlayVersion.value = 0;
  idSequence = 0;
}
